import React, { useState } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'wunsch', label: 'Wunsch / Feature', icon: '✨' },
  { id: 'bug', label: 'Fehler / Bug', icon: '🐞' },
  { id: 'kurs', label: 'Kurs-Feedback', icon: '📚' },
  { id: 'sonstiges', label: 'Sonstiges', icon: '💬' },
];

/**
 * Schwebender „Was fehlt dir noch?"-Button unten rechts.
 * Öffnet ein kompaktes Formular; sendet Feedback an /api/feedback.
 */
export const FeedbackWidget = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('wunsch');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (message.trim().length < 3) {
      toast({ title: 'Zu kurz', description: 'Bitte schreib mindestens 3 Zeichen.', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          category,
          email: email.trim() || undefined,
          context_url: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        toast({ title: 'Danke!', description: 'Martin liest jede Rückmeldung persönlich.' });
        setMessage('');
        setEmail('');
        setOpen(false);
      } else {
        toast({ title: 'Fehler', description: data.detail || 'Bitte später erneut versuchen.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Netzwerkfehler', description: 'Bitte später erneut versuchen.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Trigger-Bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold px-5 py-3 shadow-2xl shadow-yellow-500/40 hover:from-yellow-400 hover:to-amber-400 hover:scale-105 transition-all"
          data-testid="feedback-widget-trigger"
          aria-label="Feedback geben"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline">Was fehlt dir?</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[calc(100%-2.5rem)] max-w-sm rounded-2xl bg-gray-950 border-2 border-yellow-500/50 shadow-2xl shadow-yellow-500/20 overflow-hidden" data-testid="feedback-widget-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border-b border-yellow-500/30">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-white font-black text-sm">Was fehlt dir noch?</p>
                <p className="text-yellow-300/80 text-[10px]">Martin liest jede Rückmeldung persönlich</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-white p-1"
              data-testid="feedback-widget-close"
              aria-label="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Kategorie-Chips */}
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-1.5" data-testid="feedback-categories">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${category === cat.id ? 'bg-yellow-500 text-black border-yellow-500 font-bold' : 'bg-gray-900 text-gray-400 border-gray-700 hover:border-yellow-500/50'}`}
                  data-testid={`feedback-cat-${cat.id}`}
                >
                  <span className="mr-1">{cat.icon}</span>{cat.label}
                </button>
              ))}
            </div>

            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Was würdest du dir wünschen? Ganz ehrlich – kurze Sätze reichen!"
              rows={4}
              className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 resize-none text-sm"
              maxLength={2000}
              data-testid="feedback-message-input"
            />

            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Deine E-Mail (optional, für Rückfragen)"
              className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 text-sm"
              data-testid="feedback-email-input"
            />

            <Button
              onClick={submit}
              disabled={sending}
              className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold"
              data-testid="feedback-submit-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Wird gesendet …' : 'Feedback senden'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
